import { collection, query, where, getDocs, Timestamp, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { createNotification } from '../../services/notificationService';
import { getWooCommerceConfig, syncProductStock } from '../../services/woocommerce';

// Função principal que roda todas as verificações
export async function checkAndCreateNotifications() {
  try {
    await Promise.all([
      checkUnprocessedSales(),
      checkPendingPayments(),
      checkLowStockProducts(),
      checkSalesGoals(),
      checkNewQuoteRequests(),
      checkNewMessages(),
      checkNewOrders(),
      syncWooCommerceStock()
    ]);
  } catch (error) {
    console.error('Erro crítico ao executar verificações:', error);
    throw error;
  }
}

// Verifica vendas não processadas
export async function checkUnprocessedSales() {
  try {
    const salesRef = collection(db, 'sales');
    const unprocessedQuery = query(
      salesRef,
      where('processed', '==', false),
      where('notified', '!=', true)
    );

    const snapshot = await getDocs(unprocessedQuery);

    // Se não houver vendas não processadas, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const sale = doc.data();
        if (!sale.companyId) continue;

        await createNotification(sale.companyId, {
          title: 'Venda Não Processada',
          content: `A venda #${sale.id} está pendente de processamento`,
          type: 'warning',
          link: `/vendas/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          notified: true,
          lastNotificationDate: Timestamp.now()
        });
      } catch (error) {
        console.error(`Error processing sale ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Critical error checking unprocessed sales:', error);
  }
}

// Verifica pagamentos pendentes
export async function checkPendingPayments() {
  try {
    const paymentsRef = collection(db, 'payments');
    const pendingQuery = query(
      paymentsRef,
      where('status', '==', 'pending'),
      where('notified', '!=', true)
    );

    const snapshot = await getDocs(pendingQuery);

    // Se não houver pagamentos pendentes, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const payment = doc.data();
        if (!payment.companyId) continue;

        await createNotification(payment.companyId, {
          title: 'Pagamento Pendente',
          content: `O pagamento #${payment.id} está pendente de confirmação`,
          type: 'warning',
          link: `/pagamentos/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          notified: true,
          lastNotificationDate: Timestamp.now()
        });
      } catch (error) {
        console.error(`Error processing payment ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Critical error checking pending payments:', error);
  }
}

// Verifica produtos com estoque baixo
export async function checkLowStockProducts() {
  try {
    const productsRef = collection(db, 'products');
    const lowStockQuery = query(
      productsRef,
      where('stockQuantity', '<=', 10),
      where('lowStockNotified', '!=', true)
    );

    const snapshot = await getDocs(lowStockQuery);

    // Se não houver produtos com estoque baixo, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const product = doc.data();
        if (!product.companyId) continue;

        await createNotification(product.companyId, {
          title: 'Alerta de Estoque Baixo',
          content: `O produto "${product.name}" está com estoque baixo (${product.stockQuantity} unidades restantes)`,
          type: 'warning',
          link: `/produtos/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          lowStockNotified: true,
          lastNotificationDate: Timestamp.now()
        });
      } catch (error) {
        console.error(`Error processing product ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Critical error checking low stock products:', error);
  }
}

// Verifica metas de vendas
export async function checkSalesGoals() {
  try {
    const goalsRef = collection(db, 'salesGoals');
    const activeGoalsQuery = query(
      goalsRef,
      where('status', '==', 'active'),
      where('notified', '!=', true)
    );

    const snapshot = await getDocs(activeGoalsQuery);

    // Se não houver metas de vendas ativas, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const goal = doc.data();
        if (!goal.companyId) continue;

        await createNotification(goal.companyId, {
          title: 'Meta de Vendas',
          content: `Meta de vendas "${goal.name}" precisa de atenção`,
          type: 'info',
          link: `/metas/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          notified: true,
          lastNotificationDate: Timestamp.now()
        });
      } catch (error) {
        console.error(`Error processing goal ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Critical error checking sales goals:', error);
  }
}

// Verifica pedidos de orçamento
export async function checkNewQuoteRequests() {
  try {
    const quotesRef = collection(db, 'quoteRequests');
    const newQuotesQuery = query(
      quotesRef,
      where('status', '==', 'new'),
      where('notified', '!=', true)
    );

    const snapshot = await getDocs(newQuotesQuery);

    // Se não houver pedidos de orçamento novos, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const quote = doc.data();
        if (!quote.companyId) continue;

        await createNotification(quote.companyId, {
          title: 'Novo Pedido de Orçamento',
          content: `Novo pedido de orçamento recebido de ${quote.customerName}`,
          type: 'info',
          link: `/orcamentos/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          notified: true,
          lastNotificationDate: Timestamp.now()
        });
      } catch (error) {
        console.error(`Error processing quote ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Critical error checking quote requests:', error);
  }
}

// Verifica novas ordens
export async function checkNewOrders() {
  try {
    const ordersRef = collection(db, 'orders');
    const newOrdersQuery = query(
      ordersRef,
      where('notified', '!=', true),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(newOrdersQuery);

    // Se não houver novas ordens, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const order = doc.data();
        if (!order.companyId) continue;

        await createNotification(order.companyId, {
          title: 'Nova Ordem Recebida',
          content: `Nova ordem #${order.id} recebida. Valor total: €${order.total}`,
          type: 'info',
          link: `/ordens/${doc.id}`
        });

        await updateDoc(doc.ref, {
          notified: true
        });
      } catch (err) {
        console.error('Erro ao processar ordem:', err);
      }
    }
  } catch (error) {
    console.error('Erro ao verificar novas ordens:', error);
    throw error;
  }
}

// Verifica novas mensagens
export async function checkNewMessages() {
  try {
    const messagesRef = collection(db, 'messages');
    const newMessagesQuery = query(
      messagesRef,
      where('read', '==', false),
      where('notified', '!=', true)
    );

    const snapshot = await getDocs(newMessagesQuery);
    
    // Se não houver novas mensagens, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const message = doc.data();
        if (!message.recipientId) continue;

        await createNotification(message.recipientId, {
          title: 'Nova Mensagem',
          content: `Você recebeu uma nova mensagem de ${message.sender}`,
          type: 'info',
          link: `/mensagens/${doc.id}`
        });

        await updateDoc(doc.ref, { 
          notified: true 
        });
      } catch (err) {
        console.error('Erro ao processar mensagem:', err);
      }
    }
  } catch (error) {
    console.error('Erro ao verificar novas mensagens:', error);
    throw error;
  }
}

// Função para sincronizar stock com WooCommerce
export async function syncWooCommerceStock() {
  try {
    // Buscar produtos que têm integração com WooCommerce
    const productsRef = collection(db, 'products');
    const productsQuery = query(
      productsRef,
      where('woocommerce.synced', '==', true)
    );

    const snapshot = await getDocs(productsQuery);

    // Se não houver produtos para sincronizar, retorna silenciosamente
    if (snapshot.empty) {
      return;
    }

    for (const doc of snapshot.docs) {
      try {
        const product = doc.data();
        const companyId = product.companyId;

        // Buscar configuração do WooCommerce
        const config = await getWooCommerceConfig(companyId);
        if (!config) {
          console.error(`Configuração WooCommerce não encontrada para empresa ${companyId}`);
          continue;
        }

        // Sincronizar stock
        if (product.woocommerce?.woocommerce_id) {
          await syncProductStock(
            config,
            product.woocommerce.woocommerce_id,
            product.stockQuantity
          );
        }
      } catch (error) {
        console.error(`Erro crítico ao sincronizar produto ${doc.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Erro crítico ao sincronizar stock com WooCommerce:', error);
  }
}
